import React, { useEffect, useRef, useState } from "react";
import { Button, Col, Form, Row, Table, Pagination } from "react-bootstrap";
import Select from "react-select";
import ReactPlayer from "react-player";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDownload,
  faPause,
  faPlay,
  faTrash,
  faVolumeHigh,
  faVolumeXmark,
} from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";

import "./Recording_List.css";
import {
  DeleteRecordingList,
  GetAllRecordingList,
} from "../../../features/admin/recording_list/Slice";
import { useDispatch, useSelector } from "react-redux";
import { IMG_URL } from "../../../utils/common";
import { DeleteHost } from "../../../features/admin/host/slice";
import CDeleteModelNew from "../../common_components/common-modal/deleteNew/CDeleteModelNew";

const Recording = () => {
  const dispatch = useDispatch();

  const playerRefs = useRef({});

  const [room, setRoom] = useState(null);
  const [studentName, setStudentName] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");

  const [selectAll, setSelectAll] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);

  const [playingId, setPlayingId] = useState(null);
  const [mutedIds, setMutedIds] = useState({});
  const [playedIds, setPlayedIds] = useState({});
  const [durationIds, setDurationIds] = useState({});

  const RecordingList = useSelector(
    (state) => state.recording_list.recording_list,
  );

  const fetchRecordings = async (page = 1) => {
    await dispatch(
      GetAllRecordingList({
        name: studentName || "",
        email: email || "",
        page,
        per_page: 10,
      }),
    );
  };

  useEffect(() => {
    fetchRecordings(1);
  }, [name, email, studentName]);

  useEffect(() => {
    return () => {
      playerRefs.current = {};
    };
  }, []);

  const handleHostPageChange = (page) => {
    if (page < 1) return;

    fetchRecordings(page);
  };

  const renderPagination = (data, handlePageChange) => {
    if (!data) return null;

    const currentPage = Number(data.current_page || 1);
    const perPage = Number(data.per_page || 10);
    const total = Number(data.total || 0);

    const lastPage = Math.ceil(total / perPage);

    if (lastPage <= 1) {
      return null;
    }

    return (
      <div className="d-flex justify-content-end mt-3">
        <Pagination>
          <Pagination.Prev
            disabled={currentPage === 1}
            onClick={() => handlePageChange(currentPage - 1)}
          />

          {Array.from({ length: lastPage }, (_, index) => {
            const page = index + 1;

            return (
              <Pagination.Item
                key={page}
                active={page === currentPage}
                onClick={() => handlePageChange(page)}
              >
                {page}
              </Pagination.Item>
            );
          })}

          <Pagination.Next
            disabled={currentPage === lastPage}
            onClick={() => handlePageChange(currentPage + 1)}
          />
        </Pagination>
      </div>
    );
  };

  const handleReset = () => {
    setRoom(null);
    setStudentName("");
    setFromDate("");
    setToDate("");

    setName("");
    setEmail("");

    setSelectedRows([]);
    setSelectAll(false);

    setPlayingId(null);
    setMutedIds({});
    setPlayedIds({});
    setDurationIds({});

    playerRefs.current = {};

    fetchRecordings(1);
  };

  const handleSearch = () => {
    setName(studentName);
  };

  const handleRefresh = () => {
    fetchRecordings(RecordingList?.current_page || 1);
  };

  const handleSelectAll = (e) => {
    const checked = e.target.checked;

    setSelectAll(checked);

    if (checked) {
      const rows = RecordingList?.data || [];

      setSelectedRows(rows.map((item) => item.id));
    } else {
      setSelectedRows([]);
    }
  };

  const handleRowSelect = (id) => {
    setSelectedRows((prev) => {
      if (prev.includes(id)) {
        const updatedRows = prev.filter((item) => item !== id);

        setSelectAll(false);

        return updatedRows;
      }

      const updatedRows = [...prev, id];

      const currentRows = RecordingList?.data || [];

      if (currentRows.length > 0 && updatedRows.length === currentRows.length) {
        setSelectAll(true);
      }

      return updatedRows;
    });
  };

  const handlePlayPause = async (id) => {
    const audio = playerRefs.current[id];

    if (!audio) {
      console.error("Audio element not found:", id);
      return;
    }

    try {
      if (playingId !== null && playingId !== id) {
        const currentAudio = playerRefs.current[playingId];

        if (currentAudio) {
          currentAudio.pause();
        }
      }

      if (playingId === id) {
        audio.pause();
        setPlayingId(null);
        return;
      }

      await audio.play();

      setPlayingId(id);
    } catch (error) {
      console.error("Audio play error:", error);
      console.error("Audio URL:", audio.src);
    }
  };

  const handlePlayerPlay = (id) => {
    setPlayingId(id);
  };

  const handlePlayerPause = (id) => {
    setPlayingId((prev) => {
      if (prev === id) {
        return null;
      }

      return prev;
    });
  };

  const handleMute = (id) => {
    setMutedIds((prev) => ({
      ...prev,
      [id]: !Boolean(prev[id]),
    }));
  };

  const formatTime = (seconds) => {
    if (
      seconds === null ||
      seconds === undefined ||
      Number.isNaN(Number(seconds))
    ) {
      return "00:00";
    }

    const totalSeconds = Math.max(0, Math.floor(Number(seconds)));

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const remainingSeconds = totalSeconds % 60;

    if (hours > 0) {
      return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
        2,
        "0",
      )}:${String(remainingSeconds).padStart(2, "0")}`;
    }

    return `${String(minutes).padStart(2, "0")}:${String(
      remainingSeconds,
    ).padStart(2, "0")}`;
  };

  const formatDate = (date) => {
    if (!date) return "-";

    const parsedDate = new Date(date);

    if (Number.isNaN(parsedDate.getTime())) {
      return date;
    }

    return parsedDate.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formatDateTime = (date) => {
    if (!date) return "-";

    const parsedDate = new Date(date);

    if (Number.isNaN(parsedDate.getTime())) {
      return "-";
    }

    return parsedDate.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  };

  const getRecordingUrl = (recording) => {
    if (!recording) {
      return "";
    }

    return IMG_URL(recording);
  };

  const handleDownload = (row) => {
    const url = getRecordingUrl(row?.recording);

    if (!url) return;

    const link = document.createElement("a");

    link.href = url;
    link.download = `recording-${row?.id || Date.now()}.webm`;

    document.body.appendChild(link);

    link.click();

    document.body.removeChild(link);
  };

  const [rdelete, setRDelete] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);
  const [deleteType, setDeleteType] = useState(null);
  const handleDeleteClick = (row) => {
    setDeleteRow(row);
    setDeleteType("single");
    setRDelete(true);
  };

  const handleDelete = async () => {
    if (!deleteRow?.id) {
      return;
    }

    try {
      await dispatch(DeleteRecordingList(deleteRow.id)).unwrap();

      setDeleteRow(null);
      setDeleteType(null);
      setRDelete(false);

      handleHostPageChange(currentPage);
    } catch (error) {
      console.error("Delete Recording List error:", error);
    }
  };

  const handleDeleteSelectedClick = () => {
    if (selectedRows.length === 0) {
      return;
    }

    setDeleteType("multiple");
    setRDelete(true);
  };

  const handleDeleteSelected = async () => {
    if (selectedRows.length === 0) {
      return;
    }

    try {
      await dispatch(DeleteRecordingList(selectedRows)).unwrap();

      setSelectedRows([]);
      setSelectAll(false);
      setDeleteType(null);
      setRDelete(false);

      handleHostPageChange(1);
    } catch (error) {
      console.error("Delete selected recordings error:", error);
    }
  };

  const handleDeleteAllClick = () => {
    if (rows.length === 0) {
      return;
    }

    setDeleteType("all");
    setRDelete(true);
  };

  const handleDeleteAll = async () => {
    const rows = RecordingList?.data || [];

    if (rows.length === 0) {
      return;
    }

    const allIds = rows.map((row) => row.id);

    try {
      await dispatch(DeleteRecordingList(allIds)).unwrap();

      setSelectedRows([]);
      setSelectAll(false);
      setDeleteType(null);
      setRDelete(false);

      handleHostPageChange(1);
    } catch (error) {
      console.error("Delete All Recordings error:", error);
    }
  };

  const handleWhatsApp = (row) => {
    const url = getRecordingUrl(row?.recording);

    if (!url) return;

    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(
      `Recording: ${url}`,
    )}`;

    window.open(whatsappUrl, "_blank", "noopener,noreferrer");
  };

  // CURRENT PAGE DATA

  const rows = RecordingList?.data || [];

  // PAGINATION

  const currentPage = Number(RecordingList?.current_page || 1);
  const perPage = Number(RecordingList?.per_page || 10);
  const total = Number(RecordingList?.total || 0);

  const showingFrom = total === 0 ? 0 : (currentPage - 1) * perPage + 1;

  const showingTo = Math.min(currentPage * perPage, total);

  return (
    <section className="Recording_List common_section">
      <div className="sectin_top">
        <h2 className="section_name">Recording List</h2>
      </div>

      <div className="section_filter_div">
        <Row className="align-items-center">
          <Col lg={2} md={6} className="mb-3 mb-lg-0">
            <Form.Control
              type="text"
              placeholder="Search by Student Name"
              value={studentName}
              onChange={(e) => setStudentName(e.target.value)}
              className="student_input"
            />
          </Col>

          {/* FROM DATE */}
          {/* <Col lg={2} md={6} className="mb-3 mb-lg-0">
            <Form.Control
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="date_input"
            />
          </Col>

       
          <Col lg={2} md={6} className="mb-3 mb-lg-0">
            <Form.Control
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="date_input"
            />
          </Col> */}

          {/* SEARCH */}
          <Col lg={2} md={12}>
            <button type="button" className="search_btn" onClick={handleSearch}>
              Search
            </button>
          </Col>
        </Row>

        <Row className="filter_bottom_row">
          <Col lg={2} md={4} sm={6}>
            <button type="button" className="reset_btn" onClick={handleReset}>
              Reset
            </button>
          </Col>

          <Col lg={2} md={4} sm={6}>
            <button
              type="button"
              className="refresh_btn"
              onClick={handleRefresh}
            >
              Refresh
            </button>
          </Col>
        </Row>
      </div>

      <div className="table_div_holder">
        <div className="delete_btn_div">
          <Button
            variant="danger"
            onClick={handleDeleteSelectedClick}
            disabled={selectedRows.length === 0}
          >
            <FontAwesomeIcon icon={faTrash} />
            Delete Selected
          </Button>

          <button
            type="button"
            className="delete_all"
            onClick={handleDeleteAllClick}
            disabled={rows.length === 0}
          >
            Delete All
          </button>
        </div>

        <div className="table-responsive custom-table-wrapper mt-2">
          <Table
            borderless
            className="custom-table text-center align-middle m-0"
          >
            <thead>
              <tr>
                <th className="checkbox_column">
                  <Form.Check
                    type="checkbox"
                    checked={selectAll}
                    onChange={handleSelectAll}
                    className="table_checkbox"
                    disabled={rows.length === 0}
                  />
                </th>

                <th>Sr. No</th>
                <th>Meeting Id</th>
                <th>Meeting Code</th>
                <th>Name</th>
                <th>Date</th>
                <th>Time</th>
                <th>Recording</th>
              </tr>
            </thead>

            <tbody>
              {rows.length > 0 ? (
                rows.map((row, index) => {
                  const rowId = row?.id;

                  const recordingUrl = getRecordingUrl(row?.recording);

                  const isPlaying = playingId === rowId;

                  const isMuted = Boolean(mutedIds[rowId]);

                  const played = Number(playedIds[rowId] || 0);

                  const duration = Number(durationIds[rowId] || 0);

                  const currentTime = played * duration;

                  return (
                    <tr key={rowId || index}>
                      <td className="checkbox_column">
                        <Form.Check
                          type="checkbox"
                          checked={selectedRows.includes(rowId)}
                          onChange={() => handleRowSelect(rowId)}
                          className="table_checkbox"
                        />
                      </td>

                      <td>{(currentPage - 1) * perPage + index + 1}</td>

                      <td className="fw-bold">
                        {row?.Hostuser?.Meeting?.id || "-"}
                      </td>

                      <td className="fw-bold">
                        {row?.Hostuser?.Meeting?.meeting_code || "-"}
                      </td>

                      <td className="fw-bold">
                        {row?.Hostuser?.User?.user_name || "-"}
                      </td>

                      <td className="fw-bold">{formatDate(row?.createdAt)}</td>

                      <td className="fw-bold">
                        {formatDateTime(row?.createdAt)}
                      </td>

                      <td>
                        {recordingUrl ? (
                          <div className="recording_player_wrapper">
                            <div className="custom_recording_player">
                              <audio
                                ref={(audio) => {
                                  if (audio) {
                                    playerRefs.current[rowId] = audio;
                                  } else {
                                    delete playerRefs.current[rowId];
                                  }
                                }}
                                src={recordingUrl}
                                muted={isMuted}
                                preload="metadata"
                                onPlay={() => {
                                  handlePlayerPlay(rowId);
                                }}
                                onPause={() => {
                                  handlePlayerPause(rowId);
                                }}
                                onEnded={() => {
                                  setPlayingId((prev) =>
                                    prev === rowId ? null : prev,
                                  );

                                  setPlayedIds((prev) => ({
                                    ...prev,
                                    [rowId]: 0,
                                  }));
                                }}
                                onTimeUpdate={(e) => {
                                  const audio = e.currentTarget;

                                  if (audio.duration) {
                                    setPlayedIds((prev) => ({
                                      ...prev,
                                      [rowId]:
                                        audio.currentTime / audio.duration,
                                    }));
                                  }
                                }}
                                onLoadedMetadata={(e) => {
                                  const audio = e.currentTarget;

                                  setDurationIds((prev) => ({
                                    ...prev,
                                    [rowId]: audio.duration || 0,
                                  }));
                                }}
                                onError={(e) => {
                                  console.error(
                                    "AUDIO ERROR:",
                                    e.currentTarget.error,
                                  );
                                  console.error("AUDIO URL:", recordingUrl);
                                }}
                                style={{
                                  display: "none",
                                }}
                              />

                              <button
                                type="button"
                                className="custom_play_btn"
                                onClick={() => {
                              

                                  handlePlayPause(rowId);
                                }}
                              >
                                <FontAwesomeIcon
                                  icon={isPlaying ? faPause : faPlay}
                                />
                              </button>

                              <div
                                className="custom_progress_wrapper"
                                onClick={(e) => {
                                  if (!duration) {
                                    return;
                                  }

                                  const rect = e.currentTarget.getBoundingClientRect();

                                  if (!rect.width) {
                                    return;
                                  }

                                  const clickPosition = e.clientX - rect.left;

                                  const percentage = clickPosition / rect.width;

                                  const safePercentage = Math.max(
                                    0,
                                    Math.min(1, percentage),
                                  );

                                  const audio = playerRefs.current[rowId];

                                  if (audio && audio.duration) {
                                    audio.currentTime =
                                      safePercentage * audio.duration;
                                  }

                                  setPlayedIds((prev) => ({
                                    ...prev,
                                    [rowId]: safePercentage,
                                  }));
                                }}
                              >
                                <div
                                  className="custom_progress"
                                  style={{
                                    width: `${Math.min(
                                      100,
                                      Math.max(0, played * 100),
                                    )}%`,
                                  }}
                                />
                              </div>

                              <span className="player_time">
                                {formatTime(currentTime)}
                              </span>

                              <span className="player_slash">/</span>

                              <span className="player_time">
                                {formatTime(duration)}
                              </span>

                              <button
                                type="button"
                                className="custom_volume_btn"
                                onClick={() => handleMute(rowId)}
                              >
                                <FontAwesomeIcon
                                  icon={isMuted ? faVolumeXmark : faVolumeHigh}
                                />
                              </button>
                            </div>

                            <div className="recording_action_buttons">
                              <Button
                                variant="link"
                                className="p-0 action-btn"
                                onClick={() => handleDownload(row)}
                                title="Download"
                              >
                                <FontAwesomeIcon
                                  icon={faDownload}
                                  className="icon-download"
                                />
                              </Button>

                              <Button
                                variant="link"
                                className="p-0 action-btn"
                                onClick={() => handleDeleteClick(row)}
                                title="Delete"
                              >
                                <FontAwesomeIcon
                                  icon={faTrash}
                                  className="icon-delete"
                                />
                              </Button>

                              <Button
                                variant="link"
                                className="p-0 action-btn"
                                onClick={() => handleWhatsApp(row)}
                                title="Share on WhatsApp"
                              >
                                <FontAwesomeIcon
                                  icon={faWhatsapp}
                                  className="icon-whatsapp"
                                />
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <span className="text-muted">No Recording</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="8" className="text-center fw-bold">
                    No Data
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </div>

      <div className="result_div">
        <p className="inner_text">
          Showing{" "}
          {RecordingList?.data?.length > 0
            ? (RecordingList.current_page - 1) * RecordingList.per_page + 1
            : 0}{" "}
          to{" "}
          {(RecordingList?.current_page - 1) * RecordingList?.per_page +
            (RecordingList?.data?.length || 0)}{" "}
          of {RecordingList?.total || 0}
        </p>
      </div>
      {renderPagination(RecordingList, handleHostPageChange)}
      {rdelete && (
        <CDeleteModelNew
          show={rdelete}
          setShow={setRDelete}
          action={
            deleteType === "multiple"
              ? handleDeleteSelected
              : deleteType === "all"
              ? handleDeleteAll
              : handleDelete
          }
          text={
            deleteType === "multiple"
              ? `Are you sure you want to delete ${
                  selectedRows.length
                } selected recording${selectedRows.length > 1 ? "s" : ""}?`
              : deleteType === "all"
              ? `Are you sure you want to delete all ${rows.length} recordings on this page?`
              : "Are you sure you want to delete this recording?"
          }
        />
      )}
    </section>
  );
};

export default Recording;
